import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";
import { FLAVORS } from "../data/products";
import { useStore } from "../context/StoreContext";
import ReviewForm from "./ReviewForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Stars = ({ n, size = "h-4 w-4", testid }) => (
  <div className="flex gap-0.5" data-testid={testid}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${size} ${i < n ? "accent-text" : "text-[var(--line)]"}`} fill={i < n ? "currentColor" : "none"} />
    ))}
  </div>
);

const ReviewCard = ({ r, badge }) => (
  <motion.figure layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card flex flex-col p-5" data-testid={`review-${r.id}`}>
    <div className="mb-3 flex items-center justify-between">
      <Stars n={r.rating} />
      <span className="text-[11px] font-semibold text-[var(--muted)]">{FLAVORS[r.flavor]?.name}</span>
    </div>
    <blockquote className="flex-1 text-sm leading-relaxed">{r.text}</blockquote>
    <figcaption className="mt-4 flex items-center justify-between border-t border-[var(--line)] pt-3">
      <div>
        <p className="text-sm font-bold">{r.name}</p>
        {r.sport && <p className="text-[11px] text-[var(--muted)]">{r.sport}</p>}
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)]">{badge}</span>
    </figcaption>
  </motion.figure>
);

const Reviews = () => {
  const { t } = useStore();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const onPosted = (review) => {
    setData((d) => {
      const reviews = [review, ...d.reviews];
      const average = Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10;
      return { reviews, count: reviews.length, average };
    });
    setOpen(false);
  };

  return (
    <section id="reviews" className="scroll-mt-28 border-t border-[var(--line)] bg-[var(--paper)]" data-testid="reviews-section">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-display text-4xl sm:text-5xl" data-testid="reviews-title">{t.reviews.title}</h2>
            <p className="mt-2 max-w-xl text-base text-[var(--muted)]">{t.reviews.sub}</p>
            {data.count > 0 && (
              <div className="mt-3 flex items-center gap-2" data-testid="reviews-summary">
                <Stars n={Math.round(data.average)} testid="reviews-stars" />
                <span className="text-sm font-bold">{data.average.toFixed(1)}</span>
                <span className="text-xs text-[var(--muted)]">
                  {t.reviews.average} · {data.count === 1 ? t.reviews.countOne : t.reviews.count.replace("{n}", data.count)}
                </span>
              </div>
            )}
          </div>
          <button onClick={() => setOpen((o) => !o)} className={`${open ? "btn-outline" : "btn-primary"} self-start px-6 py-3 text-sm`} data-testid="review-open-form">
            {open ? t.reviews.cancel : t.reviews.write}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
              <ReviewForm onPosted={onPosted} />
            </motion.div>
          )}
        </AnimatePresence>

        {data.count === 0 ? (
          <div className="border border-dashed border-[var(--line)] p-8 text-center text-sm text-[var(--muted)]" data-testid="reviews-empty">
            {t.reviews.empty}
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
            {data.reviews.map((r) => <ReviewCard key={r.id} r={r} badge={t.reviews.badge} />)}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Reviews;
